/**
 * auth-backdrop.tsx — Nền giả lập giao diện hệ thống (cột menu + Hộp thư: danh sách hội thoại, khung chat, hồ sơ khách)
 * phía sau hộp đăng nhập. Hoàn toàn TĨNH: dữ liệu mẫu cố định, không gọi API, không nhận thao tác (aria-hidden + inert).
 */
import {
  BarChart3, Bot, Contact, Inbox, LayoutGrid, MessageSquare, Search, Send, Settings, Smile, Tag, Workflow,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

const RAIL = [Inbox, Contact, Bot, Workflow, BarChart3, LayoutGrid, Settings]

// Dữ liệu mẫu minh hoạ dưới đây (tên khách, nội dung chat demo) không phải UI copy thật — giữ nguyên tiếng Việt
// như ảnh chụp màn hình minh hoạ, không dịch (mỗi dòng đánh dấu i18n-ignore).
const CONVERSATIONS = [
  { name: 'Nguyễn Minh Anh', msg: 'Shop ơi còn size M màu be không ạ?', time: '09:41', unread: 2, color: 'bg-sky-400' }, // i18n-ignore
  { name: 'Trần Hoàng Long', msg: 'Mình muốn đặt 2 bộ, giao trong hôm nay được không?', time: '09:32', unread: 1, color: 'bg-emerald-400' }, // i18n-ignore
  { name: 'Lê Thu Hà', msg: 'Cảm ơn shop, mình nhận được hàng rồi nhé', time: '09:15', unread: 0, color: 'bg-amber-400' }, // i18n-ignore
  { name: 'Phạm Quốc Bảo', msg: 'Bên mình có xuất hoá đơn VAT không?', time: 'Hôm qua', unread: 0, color: 'bg-violet-400' }, // i18n-ignore
  { name: 'Võ Ngọc Trâm', msg: 'Gửi mình bảng giá sỉ với ạ', time: 'Hôm qua', unread: 0, color: 'bg-rose-400' }, // i18n-ignore
  { name: 'Đặng Gia Huy', msg: 'Ok bạn, mai mình qua lấy', time: 'T2', unread: 0, color: 'bg-cyan-400' }, // i18n-ignore
  { name: 'Bùi Khánh Linh', msg: 'Mã giảm giá còn dùng được không shop?', time: 'T2', unread: 0, color: 'bg-orange-400' }, // i18n-ignore
]

function Avatar({ name, color, size = 'h-9 w-9' }: { name: string; color: string; size?: string }) {
  const initials = name.split(' ').slice(-2).map((w) => w[0]).join('')
  return (
    <span className={`flex ${size} shrink-0 items-center justify-center rounded-full ${color} text-xs font-semibold text-white`}>
      {initials}
    </span>
  )
}

function Bar({ w, className = '' }: { w: string; className?: string }) {
  return <span className={`block h-2.5 rounded-full bg-muted ${w} ${className}`} />
}

export function AuthBackdrop() {
  const { t } = useTranslation('authPages')
  const active = CONVERSATIONS[0]
  return (
    <div
      aria-hidden="true"
      // inert: không nhận focus / chuột (React 19 hỗ trợ thuộc tính boolean).
      inert
      className="pointer-events-none fixed inset-0 flex select-none overflow-hidden bg-shell"
    >
      {/* Cột menu icon */}
      <aside className="hidden w-[60px] shrink-0 flex-col items-center gap-2 py-3 sm:flex">
        <span className="mb-3 flex h-9 w-9 items-center justify-center">
          <img src="/assets/logo-mark.png" alt="" className="h-7 w-7 object-contain" />
        </span>
        {RAIL.map((Icon, i) => (
          <span
            key={i}
            className={`flex h-10 w-10 items-center justify-center rounded-lg ${i === 0 ? 'bg-white/80 text-primary shadow-sm' : 'text-sidebar-foreground/70'}`}
          >
            <Icon className="h-5 w-5" />
          </span>
        ))}
        <span className="mt-auto h-8 w-8 rounded-full bg-primary/70" />
      </aside>

      <div className="flex min-w-0 flex-1 gap-2 p-2 sm:pl-0">
        {/* Danh sách hội thoại */}
        <section className="flex w-full shrink-0 flex-col rounded-xl bg-background sm:w-80">
          <div className="flex items-center justify-between px-4 pb-2 pt-4">
            <span className="text-base font-semibold">{t('backdrop.inbox')}</span>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="mx-4 mb-3 flex h-9 items-center gap-2 rounded-lg bg-muted px-3 text-xs text-muted-foreground">
            <Search className="h-3.5 w-3.5" /> {t('backdrop.searchPlaceholder')}
          </div>
          <div className="flex gap-4 border-b px-4 pb-2 text-xs">
            <span className="font-medium text-primary">{t('backdrop.all')}</span>
            <span className="text-muted-foreground">{t('backdrop.unread')}</span>
            <span className="text-muted-foreground">{t('backdrop.mine')}</span>
          </div>
          <ul className="flex-1 overflow-hidden">
            {CONVERSATIONS.map((c, i) => (
              <li key={c.name} className={`flex gap-3 px-4 py-3 ${i === 0 ? 'bg-primary/5' : ''}`}>
                <Avatar name={c.name} color={c.color} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium">{c.name}</span>
                    <span className="shrink-0 text-[11px] text-muted-foreground">{c.time}</span>
                  </div>
                  <div className="mt-0.5 flex items-center justify-between gap-2">
                    <span className="truncate text-xs text-muted-foreground">{c.msg}</span>
                    {c.unread > 0 && (
                      <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] text-white">{c.unread}</span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* Khung chat */}
        <section className="hidden min-w-0 flex-1 flex-col rounded-xl bg-background md:flex">
          <div className="flex items-center gap-3 border-b px-5 py-3">
            <Avatar name={active.name} color={active.color} size="h-8 w-8" />
            <div>
              <div className="text-sm font-medium">{active.name}</div>
              <div className="text-[11px] text-muted-foreground">{t('backdrop.zaloOaActive')}</div>
            </div>
          </div>
          <div className="flex-1 space-y-4 overflow-hidden px-5 py-5">
            <div className="max-w-[60%] rounded-2xl rounded-tl-sm bg-muted px-4 py-2.5 text-sm">Chào shop, mẫu áo này còn hàng không ạ?{/* i18n-ignore */}</div>
            <div className="ml-auto max-w-[60%] rounded-2xl rounded-tr-sm bg-primary px-4 py-2.5 text-sm text-primary-foreground">Dạ còn ạ, chị cần size nào để em kiểm tra kho giúp chị nhé!{/* i18n-ignore */}</div>
            <div className="max-w-[60%] rounded-2xl rounded-tl-sm bg-muted px-4 py-2.5 text-sm">{active.msg}</div>
            <div className="ml-auto max-w-[45%] space-y-2 rounded-2xl rounded-tr-sm bg-primary/10 px-4 py-3">
              <Bar w="w-full" className="bg-primary/20" />
              <Bar w="w-2/3" className="bg-primary/20" />
            </div>
          </div>
          <div className="m-4 flex items-center gap-3 rounded-xl border px-4 py-3 text-xs text-muted-foreground">
            <Smile className="h-4 w-4" />
            <span className="flex-1">{t('backdrop.typeMessage')}</span>
            <Send className="h-4 w-4 text-primary" />
          </div>
        </section>

        {/* Hồ sơ khách hàng */}
        <section className="hidden w-72 shrink-0 flex-col gap-4 rounded-xl bg-background p-5 xl:flex">
          <div className="flex flex-col items-center gap-2 border-b pb-4">
            <Avatar name={active.name} color={active.color} size="h-14 w-14" />
            <span className="text-sm font-semibold">{active.name}</span>
            <span className="text-xs text-muted-foreground">{t('backdrop.potentialCustomer')}</span>
          </div>
          {[t('backdrop.fieldPhone'), t('backdrop.fieldEmail'), t('backdrop.fieldSource'), t('backdrop.fieldOwner')].map((k) => (
            <div key={k} className="space-y-1.5">
              <span className="text-xs text-muted-foreground">{k}</span>
              <Bar w="w-3/4" />
            </div>
          ))}
          <div className="flex flex-wrap gap-1.5 pt-1">
            {[t('backdrop.tagVip'), t('backdrop.tagPurchased'), 'Zalo'].map((tag) => (
              <span key={tag} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary">
                <Tag className="h-3 w-3" /> {tag}
              </span>
            ))}
          </div>
        </section>
      </div>
    </div>
  )
}
